import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { type ErrorRecord } from 'src/hooks/useForm';
import JobsService, { type JobPayload } from 'src/services/jobsService';
import SitesService from 'src/services/sites/sitesService';
import TasksService from 'src/services/tasks/taskService';
import { type Region, type RegionTask } from 'src/services/tasks/types';

import { type Site } from './siteSlice';

import { type JobConfigTableDataRow } from './jobConfigPageSlice/types';

// **** Constants **** //

const jobFieldLengthValues: JobFieldLength = {
  maxJobNameCharLength: 255,
  maxDescriptionCharLength: 200,
};

export const initialFormData: JobConfigTableDataRow = {
  id: undefined,
  name: '',
  region: '',
  jobType: 'Process Path',
  siteType: '',
  active: false,
  description: '',
  jobApplicability: 'siteType',
  pushToAllSites: false,
  applicableSites: [],
  tasks: [],
  isForTierTwoPlusRoles: false,
  isTierTwoPlusJobModified: false,
};

const initialState: CreateNewJobPageState = {
  regions: [],
  tasks: [],
  types: [],
  siteTypes: [],
  specificSites: [],
  jobFormData: initialFormData,
  requirementFormErrors: {},
  restrictionFormName: '',
  jobFieldLengths: jobFieldLengthValues,
};

// **** Types **** //

type CreateNewJobPageState = {
  regions: Region[];
  types: Type[];
  tasks: RegionTask[];
  siteTypes: SiteType[];
  specificSites: Site[];
  jobFormData: JobConfigTableDataRow;
  jobFormErrors?: ErrorRecord<JobConfigTableDataRow>;
  requirementFormErrors: { [key: string]: ErrorRecord<JobTask> };
  restrictionFormName: string;
  jobFieldLengths: JobFieldLength;
};

export interface JobTask extends RegionTask {
  bodyLocationType: string;
  requirementName: string;
}

export interface Type {
  name: string;
  id: number;
}

export interface SiteType {
  label: string;
  id: number;
}

export interface JobFieldLength {
  maxJobNameCharLength: number;
  maxDescriptionCharLength: number;
}

// **** Functions **** //

export const jobPayloadToTableRow = (job: JobPayload): JobConfigTableDataRow => {
  const row = {
    id: job?.id,
    name: job?.name,
    description: job?.description ?? '',
    jobType: job?.jobType?.name,
    siteType: job?.siteType?.label,
    region: job?.region?.name,
    applicableSites: job?.specificSites?.map((s) => s.site),
    active: job?.active,
    jobApplicability: job?.siteType ? 'siteType' : 'siteSpecific',
    tasks: job?.jobRequirements.map((r) => ({
      taskId: r?.task.id,
      label: r?.task.label,
      bodyLocationType: r?.bodyLocation,
      identifyBodyLocation: r?.bodyLocation ? true : false,
      requirementName: r?.task.measureUnit?.name === 'YES/NO' ? (r?.value === '0' ? 'No' : 'Yes') : r?.value,
      measureUnit: r?.task?.measureUnit?.name,
    })),
    isForTierTwoPlusRoles: job?.isForTierTwoPlusRoles,
    isTierTwoPlusJobModified: job?.isTierTwoPlusJobModified,
  } as JobConfigTableDataRow;
  return row;
};

// **** Setup API Calls **** //

/**
 * Fetch regions
 */
export const fetchRegions = createAsyncThunk('jobs/fetchRegions', async () => {
  return TasksService.fetchRegions();
});

/**
 * Get specific sites
 */
export const getSpecificSites = createAsyncThunk('jobs/getSpecificSites', async (regionId: number) => {
  const { data } = await SitesService.getRegionSites(regionId);
  return data;
});

/**
 * Get job types
 */
export const getJobTypes = createAsyncThunk('jobs/getJobTypes', async () => {
  const { data } = await JobsService.getJobTypes();
  return data;
});

/**
 * Fetch RegionTasks by RegionId
 */
export const fetchRegionTasksByRegionId = createAsyncThunk(
  'jobs/fetchRegionTasksByRegionId',
  async (regionId: number) => {
    return TasksService.fetchRegionTasksByRegionId(regionId);
  },
);

/**
 * Get site types
 */
export const getBuildingTypes = createAsyncThunk('jobs/getBuildingTypes', async () => {
  const { data } = await SitesService.getBuildingTypes();
  return data;
});

/**
 * Get job by id
 */
export const getJobById = createAsyncThunk('jobs/getJobById', async (jobId: number) => {
  const { data } = await JobsService.getJobById(jobId);
  return data;
});

// **** Setup **** //

const { reducer, actions } = createSlice({
  name: 'jobConfigPageSlice',
  initialState,
  reducers: {
    setJobFormData: (state, action: PayloadAction<JobConfigTableDataRow>) => {
      state.jobFormData = action.payload;
    },
    setJobFormErrors: (state, action) => {
      state.jobFormErrors = action.payload;
    },
    setRequirementFormErrors: (state, action) => {
      state.requirementFormErrors = action.payload;
    },
    setRestrictionFormName: (state, action) => {
      state.restrictionFormName = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getBuildingTypes.fulfilled, (state, { payload }) => {
      state.siteTypes = [...payload].sort((a: SiteType, b: SiteType) => {
        return a.label.localeCompare(b.label);
      });
    });
    builder.addCase(getJobTypes.fulfilled, (state, { payload }) => {
      state.types = payload;
    });
    builder.addCase(fetchRegions.fulfilled, (state, { payload }) => {
      state.regions = payload;
    });
    builder.addCase(fetchRegionTasksByRegionId.fulfilled, (state, { payload }) => {
      state.tasks = payload;
    });
    builder.addCase(getSpecificSites.fulfilled, (state, { payload }) => {
      state.specificSites = payload;
    });
    builder.addCase(getJobById.fulfilled, (state, { payload }) => {
      state.jobFormData = jobPayloadToTableRow(payload);
    });
  },
});

// **** Export **** //

export const { setJobFormData, setJobFormErrors, setRequirementFormErrors, setRestrictionFormName } = actions;
export default reducer;
